import React, { useState, useEffect } from "react";

interface SplashScreenProps {
  onComplete: () => void;
  isReady: boolean;
}

const SplashScreen: React.FC<SplashScreenProps> = ({ onComplete, isReady }) => {
  const [phase, setPhase] = useState<"welcome" | "loading" | "fadeout">(
    "welcome",
  );
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Welcome phase: show for 1.2 seconds
    const welcomeTimer = setTimeout(() => {
      setPhase("loading");
    }, 1200);

    return () => clearTimeout(welcomeTimer);
  }, []);

  useEffect(() => {
    // When app is ready and we're in loading phase, start fade out
    if (isReady && phase === "loading") {
      const fadeTimer = setTimeout(() => {
        setPhase("fadeout");
        // Remove splash after fade animation
        setTimeout(() => {
          setVisible(false);
          onComplete();
        }, 600);
      }, 800);

      return () => clearTimeout(fadeTimer);
    }
  }, [isReady, phase, onComplete]);

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-white transition-opacity duration-600 ${
        phase === "fadeout" ? "opacity-0" : "opacity-100"
      }`}
    >
      <div className="text-center px-6">
        {/* Welcome Message */}
        <div
          className={`transition-all duration-700 ${
            phase === "welcome"
              ? "opacity-100 translate-y-0"
              : "opacity-0 -translate-y-4"
          }`}
        >
          <p className="text-3xl md:text-4xl font-light text-gray-500">
            Welcome
          </p>
        </div>

        {/* Loading Text */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ${
            phase === "loading"
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4"
          }`}
        >
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 mb-4">
              <div
                className="w-2 h-2 bg-primary rounded-full animate-bounce"
                style={{ animationDelay: "0ms" }}
              />
              <div
                className="w-2 h-2 bg-primary rounded-full animate-bounce"
                style={{ animationDelay: "150ms" }}
              />
              <div
                className="w-2 h-2 bg-primary rounded-full animate-bounce"
                style={{ animationDelay: "300ms" }}
              />
            </div>
            <p className="text-gray-500 text-base md:text-lg font-light">
              Loading your adventure ...
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;
